import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { Terminal, BarChart2, PenTool, Users, Briefcase, UserPlus } from 'lucide-react';

const WORKFLOWS = [
  {
    id: 'engineering',
    label: 'Engineering',
    title: 'Autonomous Dev Loops',
    description: 'Agents write code, check docs, and preview UI in parallel.',
    icon: Terminal,
    color: 'emerald',
    image: '/images/illustrations/os-engineering.avif'
  },
  {
    id: 'product',
    label: 'Product',
    title: 'Live Roadmapping',
    description: 'Sync user feedback directly to backlog priorities.',
    icon: BarChart2,
    color: 'violet',
    image: '/images/illustrations/os-product.avif'
  },
  {
    id: 'marketing',
    label: 'Marketing',
    title: 'Campaign Generation',
    description: 'From strategy to asset creation (video/copy) in one graph.',
    icon: PenTool,
    color: 'rose',
    image: '/images/illustrations/os-marketing.avif'
  },
  {
    id: 'management',
    label: 'Management',
    title: 'Sprint Orchestration',
    description: 'Auto-schedule blockers and manage timeline risks.',
    icon: Users,
    color: 'blue',
    image: '/images/illustrations/os-management.avif'
  },
  {
    id: 'sales',
    label: 'Sales',
    title: 'Deal Flow CRM',
    description: 'Real-time objection handling and prospect intelligence.',
    icon: Briefcase,
    color: 'amber',
    image: '/images/illustrations/os-sales.avif'
  },
  {
    id: 'recruiting',
    label: 'HR',
    title: 'Talent Pipeline',
    description: 'Screen resumes and schedule interviews autonomously.',
    icon: UserPlus,
    color: 'cyan',
    image: '/images/illustrations/os-recruiting.avif'
  },
];

const AUTOPLAY_DURATION = 5000;

export const CanvasOSCarousel = () => {
  const [activeIdx, setActiveIdx] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Function to reset timer when user manually clicks
  const resetTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActiveIdx((prev) => (prev + 1) % WORKFLOWS.length);
    }, AUTOPLAY_DURATION);
  };

  const handleManualSwitch = (idx: number) => {
    setActiveIdx(idx);
    resetTimer(); // Restart the 5s count so it doesn't jump immediately
  };

  useEffect(() => {
    resetTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const activeItem = WORKFLOWS[activeIdx];

  return (
    <div className="w-full max-w-6xl mx-auto">
      
      {/* THE OS WINDOW FRAME */}
      <div className="bg-slate-900 border-2 border-black rounded-2xl overflow-hidden shadow-[8px_8px_0_0_rgba(17,24,39,1)] flex flex-col relative">
        
        {/* WINDOW HEADER */}
        <div className="h-10 bg-slate-100 border-b-2 border-black flex items-center px-4 justify-between select-none z-20 relative">
          <div className="flex gap-2">
            <div className="w-3 h-3 rounded-full bg-slate-300 border border-black/20" />
            <div className="w-3 h-3 rounded-full bg-slate-300 border border-black/20" />
            <div className="w-3 h-3 rounded-full bg-slate-300 border border-black/20" />
          </div>
          <div className="flex items-center gap-2 opacity-50">
             <div className="w-4 h-4 rounded bg-slate-300" />
             <div className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest">
                Canvas_OS // {activeItem.id.toUpperCase()}
            </div>
          </div>
          <div className="w-10" /> 
        </div>

        {/* MAIN DISPLAY AREA */}
        <div className="relative aspect-[16/9] md:aspect-[21/9] lg:aspect-[16/9] bg-slate-100 overflow-hidden group">
            
            <AnimatePresence mode='wait'>
                <motion.img
                    key={activeItem.id}
                    src={activeItem.image}
                    alt={activeItem.title}
                    className="w-full h-full object-cover"
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                />
            </AnimatePresence>

            {/* Gradient Overlay for Text Readability */}
            <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-slate-900 via-slate-900/90 to-transparent pointer-events-none" />

            {/* FLOATING TEXT CONTENT */}
            <div className="absolute bottom-0 left-0 w-full p-6 md:p-10 z-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
                
                <motion.div
                    key={activeItem.id + "_text"}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className="max-w-xl"
                >
                    <div className={clsx(
                        "inline-flex items-center gap-2 px-3 py-1 rounded border border-white/20 backdrop-blur-md mb-3",
                        "text-[10px] font-bold uppercase tracking-widest text-white bg-slate-800/50"
                    )}>
                        <activeItem.icon size={14} className={clsx(
                             activeItem.color === 'emerald' && "text-emerald-400",
                             activeItem.color === 'violet' && "text-violet-400",
                             activeItem.color === 'rose' && "text-rose-400",
                             activeItem.color === 'blue' && "text-blue-400",
                             activeItem.color === 'amber' && "text-amber-400",
                             activeItem.color === 'cyan' && "text-cyan-400",
                        )} />
                        {activeItem.label} Node
                    </div>
                    <h3 className="text-2xl md:text-4xl font-black text-white mb-2 leading-tight tracking-tight">
                        {activeItem.title}
                    </h3>
                    <p className="text-slate-300 text-sm md:text-lg font-medium leading-relaxed">
                        {activeItem.description}
                    </p>
                </motion.div>

                {/* Optional: Add a subtle 'Live' indicator or decorative element here if needed */}
            </div>
        </div>

        {/* THUMBNAIL NAVIGATION BAR */}
        <div className="bg-white border-t-2 border-black p-3 md:p-4">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3 md:gap-4">
                {WORKFLOWS.map((item, idx) => {
                    const isActive = activeIdx === idx;
                    const Icon = item.icon;
                    return (
                        <button
                            key={item.id}
                            onClick={() => handleManualSwitch(idx)}
                            className="group flex flex-col gap-2 text-left relative"
                        >
                            {/* MINI WINDOW PREVIEW */}
                            <div className={clsx(
                                "relative w-full aspect-[4/3] rounded-lg border-2 overflow-hidden transition-all duration-300",
                                isActive 
                                    ? "border-black shadow-[4px_4px_0_0_#1e293b] -translate-y-1 scale-105 z-10" 
                                    : "border-slate-200 grayscale opacity-70 group-hover:opacity-100 group-hover:grayscale-0 group-hover:border-slate-400"
                            )}>
                                <img src={item.image} alt={item.label} className="w-full h-full object-cover" />
                                
                                {/* App Icon Badge */}
                                <div className={clsx(
                                    "absolute bottom-1 right-1 p-1 rounded border border-black/10 bg-white/90 backdrop-blur transition-colors",
                                    isActive ? "text-slate-900" : "text-slate-400"
                                )}>
                                    <Icon size={12} strokeWidth={3} />
                                </div>
                                
                                {/* Active Overlay Tint */}
                                {isActive && (
                                    <div className="absolute inset-0 bg-emerald-500/10 pointer-events-none mix-blend-multiply" />
                                )}
                            </div>

                            {/* LABEL & PROGRESS */}
                            <div className="px-1">
                                <span className={clsx(
                                    "text-[10px] font-bold uppercase tracking-wider font-mono block",
                                    isActive ? "text-slate-900" : "text-slate-400 group-hover:text-slate-600"
                                )}>
                                    {item.label}
                                </span>
                                
                                {/* Progress Bar Line */}
                                <div className="h-0.5 w-full bg-slate-100 mt-1.5 rounded-full overflow-hidden">
                                    {isActive && (
                                        <motion.div 
                                            key={activeIdx} // Re-mount to restart animation
                                            className="h-full bg-emerald-500"
                                            initial={{ width: "0%" }}
                                            animate={{ width: "100%" }}
                                            transition={{ duration: AUTOPLAY_DURATION / 1000, ease: "linear" }}
                                        />
                                    )}
                                </div>
                            </div>
                        </button>
                    )
                })}
            </div>
        </div>

      </div>
    </div>
  );
};